import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Link } from 'react-router-dom'
import {
  CheckCircle,
  Clock,
  Hourglass,
  Rocket,
  Package,
  ListChecks,
} from 'lucide-react'
import { AnalyticsLayout } from '@/components/analytics/AnalyticsLayout'
import { KPICard, KPICardData, KPICardSkeleton } from '@/components/shared/KPICard'
import { ChartChrome } from '@/components/shared/ChartChrome'
import {
  AnalyticsEmptyState,
  AnalyticsErrorBlock,
  AnalyticsLoadingBlock,
} from '@/components/shared/AnalyticsStates'
import { ExportButton } from '@/components/shared/ExportButton'
import { useAnalyticsQuery } from '@/hooks/useAnalyticsQuery'
import { useAnalyticsFilters } from '@/contexts/AnalyticsFiltersContext'
import { useFilterDispatch } from '@/contexts/useFilterDispatch'
import {
  ConcentrationRiskPanel,
  ConcentrationRiskData,
} from '@/components/analytics/ConcentrationRiskPanel'
import {
  ForecastChart,
  RevenueForecastPoint,
} from '@/components/analytics/ForecastChart'
import { euro, int, pct } from '@/lib/format'

// ---------------- response shapes ----------------

interface FunnelResponse {
  listed: number
  reserved: number
  sold: number
  overallConversionPct: number
  reservationRatePct: number
  closingRatePct: number | null
  periodStart: string
  periodEnd: string
}

interface TimeMetrics {
  medianTimeToReservationDays: number
  medianTimeToSaleDays: number
  medianSpeedToFirstReservationDays: number
  sampleSizeSold: number
  sampleSizeReserved: number
}

interface ThroughputPoint {
  weekStart: string
  listings: number
  reservations: number
  sales: number
}

interface StaleUnit {
  unitId: number
  projectId: number
  projectName: string
  unitRef: string | null
  city: string | null
  typology: string | null
  currentPrice: number | null
  listingDate: string | null
  daysOnMarket: number
}

interface StaleResponse {
  count: number
  totalInventoryValue: number
  sample: StaleUnit[]
}

interface AbsorptionCohortRow {
  cohortMonth: string
  listed: number
  sold30d: number
  sold60d: number
  sold90d: number
  sold180d: number
  pct30d: number
  pct60d: number
  pct90d: number
  pct180d: number
}

interface SelloutForecastRow {
  projectId: number
  projectName: string
  city: string | null
  availableUnits: number
  soldLast90d: number
  projectedDays: number | null
  projectedDate: string | null
  pipelineValue: number
}

// ---------------- helpers ----------------

/** Pipeline-specific date renderer (kept local; format.ts handles numbers). */
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

// ---------------- sections ----------------

function FunnelRow() {
  const funnel = useAnalyticsQuery<FunnelResponse>(
    'pipeline-funnel',
    '/analytics/pipeline/funnel',
  )
  const time = useAnalyticsQuery<TimeMetrics>(
    'pipeline-time-metrics',
    '/analytics/pipeline/time-metrics',
  )

  if (funnel.isError || time.isError) {
    return (
      <div className="card">
        <AnalyticsErrorBlock
          title="Failed to load pipeline KPIs"
          error={funnel.error ?? time.error}
          onRetry={() => {
            void funnel.refetch()
            void time.refetch()
          }}
        />
      </div>
    )
  }

  if (funnel.isLoading || !funnel.data || time.isLoading || !time.data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <KPICardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const cards: KPICardData[] = [
    {
      id: 'listed',
      label: 'Listed',
      value: int(funnel.data.listed),
      icon: ListChecks,
      color: 'blue',
      detail: 'Period events',
      info: {
        definition:
          'Number of distinct units that entered the market (first listable appearance) during the selected period. Forms the top of the sales funnel.',
        formula: 'COUNT(unit) WHERE listing_date BETWEEN period_start AND period_end',
        calculation:
          'Listing date is stamped the first time a unit is scraped with status available or reserved. Each unit counts once regardless of later price or state changes.',
        source: 'unit.listing_date',
      },
    },
    {
      id: 'reserved',
      label: 'Reserved',
      value: int(funnel.data.reserved),
      icon: Package,
      color: 'amber',
      detail: `${pct(funnel.data.reservationRatePct)} of listed`,
      info: {
        definition:
          'Units that showed a reservation event during the period — includes units that went straight from reserved to sold. Middle stage of the funnel.',
        formula:
          'COUNT(DISTINCT unit) FROM unit_history WHERE field = "status" AND new_value = "reserved" AND changed_at BETWEEN period_start AND period_end',
        calculation:
          'Reservation is detected via the status transition in unit_history (not just current status). Reservation rate = reserved / listed × 100. A unit reserved multiple times in the period counts once.',
        source: 'unit_history',
      },
    },
    {
      id: 'sold',
      label: 'Sold',
      value: int(funnel.data.sold),
      icon: CheckCircle,
      color: 'green',
      detail: `${pct(funnel.data.overallConversionPct)} conversion`,
      info: {
        definition:
          'Units that closed during the selected period — bottom of the funnel. Drives the overall conversion % (sold / listed) shown in the subtitle.',
        formula: 'COUNT(unit) WHERE status = "sold" AND sales_date BETWEEN period_start AND period_end',
        calculation:
          'Conversion is computed on the current period cohort (sold / listed × 100). Closing rate (sold / reserved × 100) is shown on the funnel breakdown chart and is null when reserved = 0.',
        source: 'unit.status, unit.sales_date',
      },
    },
    {
      id: 'time-to-sale',
      label: 'Median days to sell',
      value: `${int(time.data.medianTimeToSaleDays)}d`,
      icon: Clock,
      color: 'amber',
      detail: `${int(time.data.sampleSizeSold)} sold`,
      info: {
        definition:
          'Typical time from listing to final sale for units sold in the period. Lower is faster absorption.',
        formula:
          'MEDIAN(sales_date - listing_date) WHERE status = "sold" AND sales_date BETWEEN period_start AND period_end',
        calculation:
          'Uses Postgres percentile_cont(0.5). The detail line shows the underlying sample size (number of sold units contributing to the median) — small samples may be volatile.',
        source: 'unit.listing_date, unit.sales_date',
      },
    },
    {
      id: 'time-to-reservation',
      label: 'Median days to reserve',
      value: `${int(time.data.medianTimeToReservationDays)}d`,
      icon: Hourglass,
      color: 'purple',
      detail: `${int(time.data.sampleSizeReserved)} reserved`,
      info: {
        definition:
          'Typical time from listing to first reservation — the classic lead-time indicator for interest velocity.',
        formula:
          'MEDIAN(reservation_date - listing_date) per unit, filtered to reservations in the period',
        calculation:
          'Reservation_date is pulled from the first status-transition-to-reserved entry in unit_history. Detail line shows sample size — small cohorts may swing the metric.',
        source: 'unit_history (status transitions), unit.listing_date',
      },
    },
    {
      id: 'speed-reservation',
      label: 'Speed to 1st reservation',
      value: `${int(time.data.medianSpeedToFirstReservationDays)}d`,
      icon: Rocket,
      color: 'green',
      detail: 'From unit_history',
      info: {
        definition:
          'Median days from project listing start to the first reservation in that project — how quickly demand kicks in after launch.',
        formula:
          'MEDIAN(first_reservation_date - project.listing_start) per project, in the period',
        calculation:
          'Per-project metric aggregated to a median. Captures the "launch velocity" of new developments. Requires both a project first-listing timestamp and a first reservation event on file.',
        source: 'project, unit_history',
      },
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {cards.map((c) => (
        <KPICard key={c.id} card={c} />
      ))}
    </div>
  )
}

function FunnelBreakdown() {
  const { data, isLoading, isError, error, refetch } = useAnalyticsQuery<FunnelResponse>(
    'pipeline-funnel',
    '/analytics/pipeline/funnel',
  )

  const chartData = data
    ? [
        { stage: 'Listed', count: data.listed },
        { stage: 'Reserved', count: data.reserved },
        { stage: 'Sold', count: data.sold },
      ]
    : []

  return (
    <ChartChrome
      title="Funnel"
      subtitle={
        data
          ? `Listed → Reserved → Sold · conversion ${pct(data.overallConversionPct)} · closing ${pct(data.closingRatePct ?? undefined)}`
          : 'Listed → Reserved → Sold'
      }
      info={{
        data: {
          definition:
            'Standard three-stage sales funnel for the selected period: how many units were listed, how many got reserved, and how many closed.',
          formula:
            'listed = COUNT unit by listing_date · reserved = COUNT distinct status→reserved transitions · sold = COUNT unit by sales_date',
          calculation:
            'Conversion % = sold / listed × 100. Closing rate % = sold / reserved × 100 (null when no reservations). Units that skipped the reserved stage still appear in sold but not reserved.',
          source: 'unit.listing_date, unit.sales_date, unit_history',
        },
      }}
      bodyMinHeight={240}
      render={(height) => {
        if (isError) {
          return (
            <AnalyticsErrorBlock
              title="Failed to load funnel"
              error={error}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        }
        if (isLoading || !data) return <AnalyticsLoadingBlock height={height} />
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v: number) => [int(v), 'Count']} />
              <Bar dataKey="count" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        )
      }}
    />
  )
}

function ThroughputChart() {
  const { data, isLoading, isError, error, refetch } = useAnalyticsQuery<ThroughputPoint[]>(
    'pipeline-throughput',
    '/analytics/pipeline/throughput',
  )

  return (
    <ChartChrome
      title="Weekly throughput (26w)"
      subtitle="Listings / reservations / sales per ISO week"
      info={{
        data: {
          definition:
            'Week-by-week supply and demand signals over the last 26 weeks. Listings = new supply; reservations = demand intent; sales = demand realised.',
          formula:
            'COUNT(unit) grouped by date_trunc(week, listing_date / status_change_reserved / sales_date)',
          calculation:
            'Weeks are Monday-aligned (Postgres ISO). Reservations are derived from unit_history status transitions. A unit can contribute to listings, reservations and sales in different weeks.',
          source: 'unit.listing_date, unit_history, unit.sales_date',
        },
      }}
      render={(height) => {
        if (isError) {
          return (
            <AnalyticsErrorBlock
              title="Failed to load throughput"
              error={error}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        }
        if (isLoading || !data) return <AnalyticsLoadingBlock height={height} />
        if (data.length === 0) {
          return (
            <AnalyticsEmptyState
              title="No throughput data yet"
              hint="Once units are listed, reserved or sold you'll see weekly counts here."
              minHeight={height}
            />
          )
        }
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="weekStart" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="listings"
                name="Listings"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="reservations"
                name="Reservations"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="sales"
                name="Sales"
                stroke="#16a34a"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )
      }}
    />
  )
}

function AbsorptionCohortsTable() {
  const { data, isLoading, isError, error, refetch } = useAnalyticsQuery<AbsorptionCohortRow[]>(
    'pipeline-absorption-cohorts',
    '/analytics/pipeline/absorption-cohorts',
  )

  return (
    <ChartChrome
      title="Absorption cohorts"
      subtitle="Listing cohort → % sold within 30 / 60 / 90 / 180 days"
      info={{
        data: {
          definition:
            'Classic cohort analysis: for units first listed in a given month, what share had sold by day 30 / 60 / 90 / 180. Lets you compare absorption curves across launch months.',
          formula:
            'For each listing month M: pct_Nd = sold_within_N_days_of_listing / listed_in_M × 100',
          calculation:
            'Each listing-month cohort is fixed at its original size; unsold units carry forward. Cohorts are truncated once the full window would exceed today (a Jan-listed cohort will show all 4 windows; a cohort listed 45 days ago will only populate 30d). Last 12 cohorts shown.',
          source: 'unit.listing_date, unit.sales_date',
        },
      }}
      bodyMinHeight={240}
      render={() => {
        if (isError) {
          return (
            <AnalyticsErrorBlock
              title="Failed to load cohorts"
              error={error}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        }
        if (isLoading || !data) return <AnalyticsLoadingBlock height={220} />
        if (data.length === 0) {
          return (
            <AnalyticsEmptyState
              title="No cohorts for current filters"
              hint="Widen the period or clear typology / city filters to see absorption cohorts."
            />
          )
        }
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-secondary-500 sticky top-0 z-10 bg-white">
                <tr>
                  <th className="text-left py-2 pr-3">Cohort</th>
                  <th className="text-right py-2 pr-3">Listed</th>
                  <th className="text-right py-2 pr-3">30d</th>
                  <th className="text-right py-2 pr-3">60d</th>
                  <th className="text-right py-2 pr-3">90d</th>
                  <th className="text-right py-2 pr-3">180d</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {data.slice(-12).map((r) => (
                  <tr key={r.cohortMonth}>
                    <td className="py-2 pr-3 font-medium text-secondary-800">{r.cohortMonth}</td>
                    <td className="py-2 pr-3 text-right">{int(r.listed)}</td>
                    <td className="py-2 pr-3 text-right">{pct(r.pct30d)}</td>
                    <td className="py-2 pr-3 text-right">{pct(r.pct60d)}</td>
                    <td className="py-2 pr-3 text-right">{pct(r.pct90d)}</td>
                    <td className="py-2 pr-3 text-right">{pct(r.pct180d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }}
    />
  )
}

function StaleInventorySection() {
  const { data, isLoading, isError, error, refetch } = useAnalyticsQuery<StaleResponse>(
    'pipeline-stale',
    '/analytics/pipeline/stale',
  )

  return (
    <ChartChrome
      title="Stale inventory"
      subtitle="Available units listed > 365 days ago"
      info={{
        data: {
          definition:
            'Available units that have been sitting on the market for more than a year. Signals overpriced / mispositioned stock and GMV at risk.',
          formula:
            'SUM(current_price), COUNT(unit) WHERE status = "available" AND (today - listing_date) > 365',
          calculation:
            'Only available status — reserved excluded. Sample rows shown are a capped preview (typically 50). Aggregate count and value are the full truth.',
          source: 'unit.listing_date, unit.current_price, unit.status',
        },
      }}
      actions={
        data ? (
          <div className="text-right text-sm">
            <div className="font-semibold text-secondary-900">{int(data.count)} units</div>
            <div className="text-secondary-500 text-xs">
              {euro(data.totalInventoryValue)} total
            </div>
          </div>
        ) : null
      }
      bodyMinHeight={240}
      render={() => {
        if (isError) {
          return (
            <AnalyticsErrorBlock
              title="Failed to load stale inventory"
              error={error}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        }
        if (isLoading || !data) return <AnalyticsLoadingBlock height={220} />
        if (data.sample.length === 0) {
          return (
            <AnalyticsEmptyState
              title="No stale units — healthy inventory"
              hint="No available units older than 365 days in the current scope."
            />
          )
        }
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-secondary-500 sticky top-0 z-10 bg-white">
                <tr>
                  <th className="text-left py-2 pr-3">Project</th>
                  <th className="text-left py-2 pr-3">Unit</th>
                  <th className="text-left py-2 pr-3">City</th>
                  <th className="text-left py-2 pr-3">Typology</th>
                  <th className="text-right py-2 pr-3">Price</th>
                  <th className="text-right py-2 pr-3">Days on market</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {data.sample.map((u) => (
                  <tr key={u.unitId}>
                    <td className="py-2 pr-3">
                      <Link
                        to={`/projects/${u.projectId}`}
                        className="text-primary-600 hover:underline"
                      >
                        {u.projectName}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">
                      <Link
                        to={`/units/${u.unitId}`}
                        className="text-primary-600 hover:underline"
                      >
                        {u.unitRef ?? `#${u.unitId}`}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">{u.city ?? '—'}</td>
                    <td className="py-2 pr-3">{u.typology ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{euro(u.currentPrice)}</td>
                    <td className="py-2 pr-3 text-right">{int(u.daysOnMarket)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }}
    />
  )
}

function SelloutForecastTable() {
  const { data, isLoading, isError, error, refetch } = useAnalyticsQuery<SelloutForecastRow[]>(
    'forecast-sellout',
    '/analytics/forecast/sellout',
  )

  return (
    <ChartChrome
      title="Projected sell-out"
      subtitle="Top projects by pipeline value · velocity = sold last 90d / 90"
      info={{
        data: {
          definition:
            "Per-project projection of when the remaining inventory will be sold at the project's current sales pace. Sorted by pipeline value (= sum of current prices of available units).",
          formula:
            'projected_days = available_units / (sold_last_90d / 90) · projected_date = today + projected_days',
          calculation:
            'Velocity is the trailing 90-day daily sales rate. Projects with no velocity (sold_last_90d = 0) show "no velocity" — absorption is undefined. Pipeline value is the GMV of the remaining available units at their current asking price.',
          source: 'unit.status, unit.sales_date, unit.current_price',
        },
      }}
      bodyMinHeight={240}
      render={() => {
        if (isError) {
          return (
            <AnalyticsErrorBlock
              title="Failed to load sell-out forecast"
              error={error}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        }
        if (isLoading || !data) return <AnalyticsLoadingBlock height={220} />
        if (data.length === 0) {
          return (
            <AnalyticsEmptyState
              title="No active pipeline for current filters"
              hint="Widen the period or clear filters to see projected sell-out."
            />
          )
        }
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-secondary-500 sticky top-0 z-10 bg-white">
                <tr>
                  <th className="text-left py-2 pr-3">Project</th>
                  <th className="text-left py-2 pr-3">City</th>
                  <th className="text-right py-2 pr-3">Available</th>
                  <th className="text-right py-2 pr-3">Sold (90d)</th>
                  <th className="text-right py-2 pr-3">Pipeline value</th>
                  <th className="text-right py-2 pr-3">Projected sell-out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {data.map((r) => (
                  <tr key={r.projectId}>
                    <td className="py-2 pr-3">
                      <Link
                        to={`/projects/${r.projectId}`}
                        className="text-primary-600 hover:underline"
                      >
                        {r.projectName}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">{r.city ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{int(r.availableUnits)}</td>
                    <td className="py-2 pr-3 text-right">{int(r.soldLast90d)}</td>
                    <td className="py-2 pr-3 text-right">{euro(r.pipelineValue)}</td>
                    <td className="py-2 pr-3 text-right">
                      {r.projectedDate ? (
                        <span title={`${r.projectedDays} days`}>{fmtDate(r.projectedDate)}</span>
                      ) : (
                        <span className="text-secondary-400" title="No sales in last 90 days">
                          no velocity
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }}
    />
  )
}

function RevenueForecastSection() {
  const { data, isLoading, isError } = useAnalyticsQuery<RevenueForecastPoint[]>(
    'forecast-revenue',
    '/analytics/forecast/revenue',
  )
  return <ForecastChart data={data} loading={isLoading} error={isError ? true : undefined} />
}

function ConcentrationRiskSection() {
  const { data, isLoading, isError } = useAnalyticsQuery<ConcentrationRiskData>(
    'concentration-risk',
    '/analytics/concentration-risk',
  )
  const { toggleIn, filters } = useFilterDispatch()
  return (
    <ConcentrationRiskPanel
      data={data}
      loading={isLoading}
      error={isError ? true : undefined}
      // Only the cities dimension is wired to a live filter in P1.2; developer
      // and broker filters arrive in P2.1 once the DTO exposes them.
      interactiveDimensions={['cities']}
      activeNames={{ cities: filters.cities }}
      onEntryClick={(dim, entry) => {
        if (dim === 'cities') toggleIn('cities', entry.name)
      }}
    />
  )
}

function PipelineBody() {
  const { queryParams } = useAnalyticsFilters()
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <ExportButton
          endpoint="/analytics/export/pipeline.csv"
          params={queryParams}
          filename="analytics-pipeline"
        />
      </div>
      <FunnelRow />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <FunnelBreakdown />
        <div className="xl:col-span-2">
          <ThroughputChart />
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <AbsorptionCohortsTable />
        <RevenueForecastSection />
      </div>
      <SelloutForecastTable />
      <StaleInventorySection />
      <ConcentrationRiskSection />
    </div>
  )
}

export function PipelineAnalytics() {
  return (
    <AnalyticsLayout>
      <PipelineBody />
    </AnalyticsLayout>
  )
}
